import { useState, useEffect } from 'react';
import { collection, query, getDocs, getDoc, doc, where, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { formatDistanceToNow, format } from 'date-fns';
import { Loader2, Trash2, ArrowUpDown, ArrowUp, ArrowDown, Download } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { bulkDeleteHabitLogEntries } from '@/lib/firebase/queries/habits';
import type { User } from '@/types';
import type { Organization } from '@/types/organization';

interface HabitLog {
  id: string;
  habitId: string;
  userId: string;
  habitName: string;
  userName: string;
  userEmail: string;
  organizationName?: string;
  organizationId?: string;
  sectionName?: string;
  groupName?: string;
  isOrganizationDefault: boolean;
  loggedAt: Date;
  category: string;
}

type SortColumn = 'userName' | 'habitName' | 'organizationName' | 'sectionName' | 'groupName' | 'loggedAt' | 'category';
type SortDirection = 'asc' | 'desc' | null;

interface LogsListProps {
  userFilter?: string[];
  typeFilter?: string[];
  categoryFilter?: string[];
  organizationFilter?: string[];
  timeFilter?: { start: Date | null; end: Date | null };
  onSelectionChange?: (selected: Set<string>) => void;
  triggerSearch?: boolean;
  onSearchComplete?: () => void;
}

export function LogsList({ 
  userFilter = [], 
  typeFilter = [], 
  categoryFilter = [],
  organizationFilter = [],
  timeFilter = { start: null, end: null },
  onSelectionChange,
  triggerSearch = false,
  onSearchComplete
}: LogsListProps) {
  const [logs, setLogs] = useState<HabitLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedLogs, setSelectedLogs] = useState<Set<string>>(new Set());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [loadingStep, setLoadingStep] = useState<string>('');
  const { t, dir, language } = useLanguage();
  const isRTL = dir === 'rtl';

  const fetchLogs = async () => {
    try {
      setIsLoading(true);
      setLoadingStep('Fetching habits...');
      
      // Use a simple query without complex filters to avoid index issues
      // We'll apply all filters at the application level
      let habitsSnapshot;
      
      // Use a simple query that doesn't require complex indexes
      // Limit to 1000 most recent habits for better performance
      const habitsQuery = query(
        collection(db, 'habits'),
        orderBy('lastLoggedAt', 'desc')
      );
      habitsSnapshot = await getDocs(habitsQuery);

      // Apply all filters at application level to avoid composite index issues
      let filteredDocs = habitsSnapshot.docs;

      // Filter out habits without lastLoggedAt (in case we used the fallback query)
      filteredDocs = filteredDocs.filter(doc => {
        const habitData = doc.data();
        return habitData.lastLoggedAt != null;
      });

      // Apply user filter
      if (userFilter.length > 0) {
        filteredDocs = filteredDocs.filter(doc => {
          const habitData = doc.data();
          return userFilter.includes(habitData.userId);
        });
      }

      // Apply type filter
      if (typeFilter.length > 0) {
        filteredDocs = filteredDocs.filter(doc => {
          const habitData = doc.data();
          if (typeFilter.includes('personal') && !typeFilter.includes('default')) {
            return !habitData.isOrganizationDefault;
          } else if (typeFilter.includes('default') && !typeFilter.includes('personal')) {
            return habitData.isOrganizationDefault;
          }
          return true;
        });
      }

      // Apply category filter
      if (categoryFilter.length > 0) {
        filteredDocs = filteredDocs.filter(doc => {
          const habitData = doc.data();
          return categoryFilter.includes(habitData.category);
        });
      }



      // Apply time filter
      if (timeFilter.start || timeFilter.end) {
        filteredDocs = filteredDocs.filter(doc => {
          const habitData = doc.data();
          const loggedAt = habitData.lastLoggedAt?.toDate();
          
          if (!loggedAt) return false;
          
          if (timeFilter.start && loggedAt < timeFilter.start) return false;
          if (timeFilter.end) {
            const endDate = new Date(timeFilter.end);
            endDate.setDate(endDate.getDate() + 1);
            if (loggedAt >= endDate) return false;
          }
          
          return true;
        });
      }

      // Limit results to prevent performance issues
      // Show only the most recent 500 logs for better performance
      filteredDocs = filteredDocs.slice(0, 500);

      // Collect unique user IDs and organization default habit IDs for batch queries
      const userIds = new Set<string>();
      const orgHabitIds = new Set<string>();
      const orgIds = new Set<string>();
      
      // Parse organization filter to extract org, section, and group IDs
      const selectedOrgIds = new Set<string>();
      const selectedSectionIds = new Set<string>();
      const selectedGroupIds = new Set<string>();
      
      organizationFilter.forEach(filter => {
        if (filter.startsWith('section_')) {
          selectedSectionIds.add(filter.replace('section_', ''));
        } else if (filter.startsWith('group_')) {
          selectedGroupIds.add(filter.replace('group_', ''));
        } else {
          selectedOrgIds.add(filter);
        }
      });
      
      filteredDocs.forEach(doc => {
        const habitData = doc.data();
        userIds.add(habitData.userId);
        
        if (habitData.isOrganizationDefault && habitData.organizationDefaultHabitId) {
          orgHabitIds.add(habitData.organizationDefaultHabitId);
        }
      });
      
      console.log(`Processing ${filteredDocs.length} habits for ${userIds.size} users...`);

      // Batch fetch all users
      setLoadingStep(`Fetching ${userIds.size} users...`);
      const usersData = new Map<string, User>();
      if (userIds.size > 0) {
        const userChunks = Array.from(userIds);
        for (let i = 0; i < userChunks.length; i += 10) { // Firestore 'in' query limit is 10
          const chunk = userChunks.slice(i, i + 10);
          const usersSnapshot = await getDocs(query(
            collection(db, 'users'),
            where('__name__', 'in', chunk)
          ));
          
          usersSnapshot.docs.forEach(doc => {
            usersData.set(doc.id, doc.data() as User);
          });
        }
      }

      // Batch fetch all organization default habits
      if (orgHabitIds.size > 0) {
        setLoadingStep(`Fetching ${orgHabitIds.size} organization habits...`);
        const orgHabitsData = new Map<string, any>();
        const orgHabitChunks = Array.from(orgHabitIds);
        for (let i = 0; i < orgHabitChunks.length; i += 10) {
          const chunk = orgHabitChunks.slice(i, i + 10);
          const orgHabitsSnapshot = await getDocs(query(
            collection(db, 'organizationDefaultHabits'),
            where('__name__', 'in', chunk)
          ));
          
          orgHabitsSnapshot.docs.forEach(doc => {
            orgHabitsData.set(doc.id, doc.data());
            if (doc.data().organizationId) {
              orgIds.add(doc.data().organizationId);
            }
          });
        }

        // Batch fetch all organizations
        const organizationsData = new Map<string, Organization>();
        if (orgIds.size > 0) {
          setLoadingStep(`Fetching ${orgIds.size} organizations...`);
          const orgChunks = Array.from(orgIds);
          for (let i = 0; i < orgChunks.length; i += 10) {
            const chunk = orgChunks.slice(i, i + 10);
            const orgsSnapshot = await getDocs(query(
              collection(db, 'organizations'),
              where('__name__', 'in', chunk)
            ));
            
            orgsSnapshot.docs.forEach(doc => {
              organizationsData.set(doc.id, doc.data() as Organization);
            });
          }
        }
        
        // Fetch group memberships to get section/group info for all users (moved outside org block)
        setLoadingStep('Fetching group memberships...');
        const groupMembersData = new Map<string, { 
          sectionId?: string; 
          groupId?: string; 
          sectionName?: string; 
          groupName?: string;
          organizationId?: string;
        }>();
        const sectionsData = new Map<string, any>();
        const groupsData = new Map<string, any>();
          
          // Batch fetch all group memberships
          const allMembershipsQuery = query(collection(db, 'groupMembers'));
          const allMembershipsSnapshot = await getDocs(allMembershipsQuery);
          
          // Collect all group IDs and parent group IDs
          const groupIds = new Set<string>();
          const parentGroupIds = new Set<string>();
          const userGroupMap = new Map<string, string>(); // userId -> groupId
          
          allMembershipsSnapshot.docs.forEach(doc => {
            const data = doc.data();
            if (userIds.has(data.userId)) {
              groupIds.add(data.groupId);
              userGroupMap.set(data.userId, data.groupId);
            }
          });
          
          // Batch fetch all groups
          if (groupIds.size > 0) {
            setLoadingStep(`Fetching ${groupIds.size} groups...`);
            const groupChunks = Array.from(groupIds);
            for (let i = 0; i < groupChunks.length; i += 10) {
              const chunk = groupChunks.slice(i, i + 10);
              const groupsSnapshot = await getDocs(query(
                collection(db, 'supportGroups'),
                where('__name__', 'in', chunk)
              ));
              
              groupsSnapshot.docs.forEach(doc => {
                const groupData = doc.data();
                groupsData.set(doc.id, groupData);
                
                // Collect parent group IDs for sections
                if (groupData.parentGroupId) {
                  parentGroupIds.add(groupData.parentGroupId);
                }
              });
            }
            
            // Batch fetch all parent groups (sections)
            if (parentGroupIds.size > 0) {
              setLoadingStep(`Fetching ${parentGroupIds.size} sections...`);
              const parentChunks = Array.from(parentGroupIds);
              for (let i = 0; i < parentChunks.length; i += 10) {
                const chunk = parentChunks.slice(i, i + 10);
                const sectionsSnapshot = await getDocs(query(
                  collection(db, 'supportGroups'),
                  where('__name__', 'in', chunk)
                ));
                
                sectionsSnapshot.docs.forEach(doc => {
                  const sectionData = doc.data();
                  sectionsData.set(doc.id, sectionData);
                });
              }
            }
            
            // Now build the group members data
            for (const [userId, groupId] of userGroupMap) {
              const groupData = groupsData.get(groupId);
              if (groupData) {
                if (groupData.groupType === 'parent') {
                  // This is a section
                  groupMembersData.set(userId, { 
                    sectionId: groupId,
                    sectionName: groupData.name || groupData.nameEn || '',
                    organizationId: groupData.organization
                  });
                } else {
                  // This is a group, find its parent section
                  if (groupData.parentGroupId) {
                    const parentGroupData = sectionsData.get(groupData.parentGroupId);
                    if (parentGroupData) {
                      groupMembersData.set(userId, { 
                        sectionId: groupData.parentGroupId,
                        groupId: groupId,
                        sectionName: parentGroupData.name || parentGroupData.nameEn || '',
                        groupName: groupData.name || groupData.nameEn || '',
                        organizationId: groupData.organization
                      });
                    }
                  } else {
                    groupMembersData.set(userId, { 
                      groupId: groupId,
                      groupName: groupData.name || groupData.nameEn || '',
                      organizationId: groupData.organization
                    });
                  }
                }
              }
            }
          }
          
          const logsData: HabitLog[] = [];
            
          for (const habitDoc of filteredDocs) {
            const habitData = habitDoc.data();
            
            if (!habitData.lastLoggedAt) continue;
            
            // Get user data from cache
            const userData = usersData.get(habitData.userId);
            if (!userData) continue;
            
            // Get organization data if it's an organization default habit
            let organizationName: string | undefined;
            let organizationId: string | undefined;
            
            if (habitData.isOrganizationDefault && habitData.organizationDefaultHabitId) {
              const orgHabitData = orgHabitsData.get(habitData.organizationDefaultHabitId);
              
              if (orgHabitData) {
                organizationId = orgHabitData.organizationId;
                
                            // Get organization name from cache
            const orgData = organizationsData.get(organizationId);
            if (orgData) {
              organizationName = language === 'ar' 
                ? (orgData.name || orgData.nameEn) 
                : (orgData.nameEn || orgData.name);
            }
              }
            }

            // Apply organization filter at application level
            if (organizationFilter.length > 0) {
              const userGroupData = groupMembersData.get(habitData.userId);
              let shouldInclude = false;
              
              for (const filter of organizationFilter) {
                if (filter.startsWith('section_')) {
                  const sectionId = filter.replace('section_', '');
                  if (userGroupData?.sectionId === sectionId) {
                    shouldInclude = true;
                    break;
                  }
                } else if (filter.startsWith('group_')) {
                  const groupId = filter.replace('group_', '');
                  if (userGroupData?.groupId === groupId) {
                    shouldInclude = true;
                    break;
                  }
                } else {
                  // Organization filter - check if user's group belongs to this organization
                  if (userGroupData?.organizationId === filter) {
                    shouldInclude = true;
                    break;
                  }
                }
              }
              
              if (!shouldInclude) {
                continue;
              }
            }

            // Reconstruct individual log entries from habit history
            const history = habitData.history || [];
            const startDate = habitData.startDate?.toDate() || new Date();
            
            history.forEach((isLogged: boolean, index: number) => {
              if (isLogged) {
                // Calculate the date for this log entry
                const logDate = new Date(startDate);
                logDate.setDate(startDate.getDate() + index);
                
                // Apply time filter to individual log entries
                if (timeFilter.start && logDate < timeFilter.start) return;
                if (timeFilter.end) {
                  const endDate = new Date(timeFilter.end);
                  endDate.setDate(endDate.getDate() + 1);
                  if (logDate >= endDate) return;
                }
                
                // Get section and group names from user's group membership
                let sectionName: string | undefined;
                let groupName: string | undefined;
                
                const userGroupData = groupMembersData.get(habitData.userId);
                if (userGroupData) {
                  sectionName = userGroupData.sectionName;
                  groupName = userGroupData.groupName;
                }
                
                logsData.push({
                  id: `${habitDoc.id}_${index}`, // Unique ID for each log entry
                  habitId: habitDoc.id,
                  userId: habitData.userId,
                  habitName: habitData.isOrganizationDefault ? habitData.name : t('type.personal'),
                  userName: userData.firstNameAr && userData.lastNameAr 
                    ? `${userData.firstNameAr} ${userData.lastNameAr}`
                    : userData.firstName && userData.lastName
                    ? `${userData.firstName} ${userData.lastName}`
                    : userData.phone || 'Unknown User',
                  userEmail: userData.phone || '',
                  organizationName,
                  organizationId,
                  sectionName,
                  groupName,
                  isOrganizationDefault: habitData.isOrganizationDefault || false,
                  loggedAt: logDate,
                  category: habitData.category
                });
              }
            });
          }
          
          setLogs(logsData);
          setHasSearched(true);
        } else {
          // No organizations to fetch, but still fetch group memberships
          setLoadingStep('Fetching group memberships...');
          const groupMembersData = new Map<string, { 
            sectionId?: string; 
            groupId?: string; 
            sectionName?: string; 
            groupName?: string;
            organizationId?: string;
          }>();
          const sectionsData = new Map<string, any>();
          const groupsData = new Map<string, any>();
          
          for (const userId of userIds) {
            const membershipQuery = query(
              collection(db, 'groupMembers'),
              where('userId', '==', userId)
            );
            const membershipSnapshot = await getDocs(membershipQuery);
            
            if (!membershipSnapshot.empty) {
              // Get the first group membership (assuming one group per user)
              const membership = membershipSnapshot.docs[0];
              const groupId = membership.data().groupId;
              
              // Get group details to determine if it's a section or group
              const groupDoc = await getDoc(doc(db, 'supportGroups', groupId));
              if (groupDoc.exists()) {
                const groupData = groupDoc.data();
                
                // Store group data
                groupsData.set(groupId, groupData);
                
                if (groupData.groupType === 'parent') {
                  // This is a section
                  sectionsData.set(groupId, groupData);
                  groupMembersData.set(userId, { 
                    sectionId: groupId,
                    sectionName: groupData.name || groupData.nameEn || '',
                    organizationId: groupData.organization
                  });
                } else {
                  // This is a group, find its parent section
                  if (groupData.parentGroupId) {
                    const parentGroupDoc = await getDoc(doc(db, 'supportGroups', groupData.parentGroupId));
                    if (parentGroupDoc.exists()) {
                      const parentGroupData = parentGroupDoc.data();
                      sectionsData.set(groupData.parentGroupId, parentGroupData);
                      
                      groupMembersData.set(userId, { 
                        sectionId: groupData.parentGroupId,
                        groupId: groupId,
                        sectionName: parentGroupData.name || parentGroupData.nameEn || '',
                        groupName: groupData.name || groupData.nameEn || ''
                      });
                    }
                  } else {
                    groupMembersData.set(userId, { 
                      groupId: groupId,
                      groupName: groupData.name || groupData.nameEn || ''
                    });
                  }
                }
              }
            }
          }
          
          const logsData: HabitLog[] = [];
            
          for (const habitDoc of filteredDocs) {
            const habitData = habitDoc.data();
            
            if (!habitData.lastLoggedAt) continue;
            
            // Get user data from cache
            const userData = usersData.get(habitData.userId);
            if (!userData) continue;
            
            // Apply organization filter at application level
            if (organizationFilter.length > 0) {
              const userGroupData = groupMembersData.get(habitData.userId);
              let shouldInclude = false;
              
              for (const filter of organizationFilter) {
                if (filter.startsWith('section_')) {
                  const sectionId = filter.replace('section_', '');
                  if (userGroupData?.sectionId === sectionId) {
                    shouldInclude = true;
                    break;
                  }
                } else if (filter.startsWith('group_')) {
                  const groupId = filter.replace('group_', '');
                  if (userGroupData?.groupId === groupId) {
                    shouldInclude = true;
                    break;
                  }
                } else {
                  // Organization filter - check if user's group belongs to this organization
                  if (userGroupData?.organizationId === filter) {
                    shouldInclude = true;
                    break;
                  }
                }
              }
              
              if (!shouldInclude) {
                continue;
              }
            }

            // Reconstruct individual log entries from habit history
            const history = habitData.history || [];
            const startDate = habitData.startDate?.toDate() || new Date();
            
            history.forEach((isLogged: boolean, index: number) => {
              if (isLogged) {
                // Calculate the date for this log entry
                const logDate = new Date(startDate);
                logDate.setDate(startDate.getDate() + index);
                
                // Apply time filter to individual log entries
                if (timeFilter.start && logDate < timeFilter.start) return;
                if (timeFilter.end) {
                  const endDate = new Date(timeFilter.end);
                  endDate.setDate(endDate.getDate() + 1);
                  if (logDate >= endDate) return;
                }
                
                // Get section and group names from group membership data
                const userGroupData = groupMembersData.get(habitData.userId);
                let sectionName: string | undefined;
                let groupName: string | undefined;
                
                if (userGroupData) {
                  if (userGroupData.sectionName) {
                    sectionName = language === 'ar' 
                      ? userGroupData.sectionName 
                      : userGroupData.sectionName;
                  }
                  if (userGroupData.groupName) {
                    groupName = language === 'ar' 
                      ? userGroupData.groupName 
                      : userGroupData.groupName;
                  }
                }
                
                logsData.push({
                  id: `${habitDoc.id}_${index}`, // Unique ID for each log entry
                  habitId: habitDoc.id,
                  userId: habitData.userId,
                  habitName: habitData.isOrganizationDefault ? habitData.name : t('type.personal'),
                  userName: userData.firstNameAr && userData.lastNameAr 
                    ? `${userData.firstNameAr} ${userData.lastNameAr}`
                    : userData.firstName && userData.lastName
                    ? `${userData.firstName} ${userData.lastName}`
                    : userData.phone || 'Unknown User',
                  userEmail: userData.phone || '',
                  organizationName: undefined,
                  organizationId: undefined,
                  sectionName,
                  groupName,
                  isOrganizationDefault: habitData.isOrganizationDefault || false,
                  loggedAt: logDate,
                  category: habitData.category
                });
              }
            });
          }
          
          setLogs(logsData);
          setHasSearched(true);
        }
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to fetch logs');
    } finally {
      setIsLoading(false);
      setLoadingStep('');
      onSearchComplete?.();
    }
  };

  // Trigger search when triggerSearch changes
  useEffect(() => {
    if (triggerSearch) {
      fetchLogs();
    }
  }, [triggerSearch]);

  // No additional filtering needed since all filters are applied at DB level
  const filteredLogs = logs;

  // Sort logs
  const sortedLogs = [...filteredLogs].sort((a, b) => {
    if (!sortColumn || !sortDirection) return 0;
    
    let aValue: any = a[sortColumn];
    let bValue: any = b[sortColumn];
    
    if (sortColumn === 'loggedAt') {
      aValue = a.loggedAt.getTime();
      bValue = b.loggedAt.getTime();
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : sortDirection === 'desc' ? null : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleSelectAll = (checked: boolean) => {
    const newSelected = checked ? new Set(sortedLogs.map(log => log.id)) : new Set();
    setSelectedLogs(newSelected);
    onSelectionChange?.(newSelected);
  };

  const handleSelectLog = (logId: string, checked: boolean) => {
    const newSelected = new Set(selectedLogs);
    if (checked) {
      newSelected.add(logId);
    } else {
      newSelected.delete(logId);
    }
    setSelectedLogs(newSelected);
    onSelectionChange?.(newSelected);
  };

  const handleDeleteSelected = async () => {
    if (selectedLogs.size === 0) return;
    
    setIsDeleting(true);
    try {
      await bulkDeleteHabitLogEntries(Array.from(selectedLogs));
      toast.success(t('deleteSuccess', { count: selectedLogs.size }));
      setSelectedLogs(new Set());
      await fetchLogs();
    } catch (error) {
      console.error('Error deleting logs:', error);
      toast.error(t('deleteError'));
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    if (sortDirection === 'asc') return <ArrowUp className="h-4 w-4 text-gray-400" />;
    if (sortDirection === 'desc') return <ArrowDown className="h-4 w-4 text-gray-400" />;
    return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
  };

  const exportToCSV = () => {
    if (sortedLogs.length === 0) {
      toast.error(t('noDataToExport'));
      return;
    }

    setIsExporting(true);
    try {
      // Define CSV headers based on language
      const headers = language === 'ar' ? [
        'اسم المستخدم',
        'رقم الهاتف',
        'اسم العادة',
        'الفئة',
        'المنظمة',
        'القسم',
        'المجموعة',
        'تاريخ التسجيل',
        'النوع'
      ] : [
        'User Name',
        'Phone',
        'Habit Name',
        'Category',
        'Organization',
        'Section',
        'Group',
        'Logged At',
        'Type'
      ];

      // Convert logs to CSV rows with proper encoding
      const csvRows = [
        headers,
        ...sortedLogs.map(log => [
          log.userName,
          log.userEmail,
          log.habitName,
          t(`habits.categories.${log.category}`),
          log.organizationName || '-',
          log.sectionName || '-',
          log.groupName || '-',
          format(log.loggedAt, 'PP'),
          log.isOrganizationDefault ? t('type.default') : t('type.personal')
        ])
      ];

      // Create CSV content with BOM for proper Arabic text encoding
      let csvContent = '';
      
      // Add BOM for UTF-8 encoding (essential for Arabic text in Excel)
      csvContent += '\uFEFF';
      
      // Convert to CSV format with proper escaping
      csvRows.forEach(row => {
        csvContent += row.map(cell => {
          // Escape quotes and wrap in quotes if contains comma, newline, or quotes
          const escapedCell = String(cell).replace(/"/g, '""');
          if (escapedCell.includes(',') || escapedCell.includes('\n') || escapedCell.includes('"')) {
            return `"${escapedCell}"`;
          }
          return escapedCell;
        }).join(',') + '\n';
      });

      // Create and download the file as CSV with UTF-8 encoding
      const blob = new Blob([csvContent], { 
        type: 'text/csv;charset=utf-8' 
      });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `habit-logs-${format(new Date(), 'yyyy-MM-dd-HH-mm')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(t('exportSuccess', { count: sortedLogs.length }));
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error(t('exportError'));
    } finally {
      setIsExporting(false);
    }
  };

  if (!hasSearched) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-4">
          <div className="text-muted-foreground">
            {t('noSearch')}
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
        {loadingStep && (
          <div className="mt-4 text-center text-muted-foreground text-sm">
            {loadingStep}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {t('showingResults', { count: sortedLogs.length, total: logs.length })}
        </div>
        <div className="flex items-center gap-2">
          {sortedLogs.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={exportToCSV}
              disabled={isExporting}
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {t('exportCSV')}
            </Button>
          )}
          {selectedLogs.size > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowDeleteDialog(true)}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              {t('deleteSelected', { count: selectedLogs.size })}
            </Button>
          )}
        </div>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12 text-center">
                <div className="flex justify-center">
                  <Checkbox
                    checked={selectedLogs.size === sortedLogs.length && sortedLogs.length > 0}
                    onCheckedChange={handleSelectAll}
                  />
                </div>
              </TableHead>
              <TableHead className={isRTL ? 'text-right' : 'text-left'}>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('userName')}
                  className="h-auto p-0 font-medium gap-2 flex-row-reverse"
                >
                  {getSortIcon('userName')}
                  {t('columns.user')} / {t('columns.phone')}
                </Button>
              </TableHead>
              <TableHead className={isRTL ? 'text-right' : 'text-left'}>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('habitName')}
                  className="h-auto p-0 font-medium gap-2 flex-row-reverse"
                >
                  {getSortIcon('habitName')}
                  {t('columns.habit')}
                </Button>
              </TableHead>
              <TableHead className={isRTL ? 'text-right' : 'text-left'}>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('category')}
                  className="h-auto p-0 font-medium gap-2 flex-row-reverse"
                >
                  {getSortIcon('category')}
                  {t('columns.category')}
                </Button>
              </TableHead>
              <TableHead className={isRTL ? 'text-right' : 'text-left'}>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('organizationName')}
                  className="h-auto p-0 font-medium gap-2 flex-row-reverse"
                >
                  {getSortIcon('organizationName')}
                  {t('columns.organization')}
                </Button>
              </TableHead>
              <TableHead className={isRTL ? 'text-right' : 'text-left'}>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('sectionName')}
                  className="h-auto p-0 font-medium gap-2 flex-row-reverse"
                >
                  {getSortIcon('sectionName')}
                  {t('columns.section')}
                </Button>
              </TableHead>
              <TableHead className={isRTL ? 'text-right' : 'text-left'}>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('groupName')}
                  className="h-auto p-0 font-medium gap-2 flex-row-reverse"
                >
                  {getSortIcon('groupName')}
                  {t('columns.group')}
                </Button>
              </TableHead>
              <TableHead className={isRTL ? 'text-right' : 'text-left'}>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('loggedAt')}
                  className="h-auto p-0 font-medium gap-2 flex-row-reverse"
                >
                  {getSortIcon('loggedAt')}
                  {t('columns.loggedAt')}
                </Button>
              </TableHead>
              <TableHead className={`w-20 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('columns.type')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedLogs.map((log) => (
              <TableRow key={log.id}>
                <TableCell className="text-center">
                  <div className="flex justify-center">
                    <Checkbox
                      checked={selectedLogs.has(log.id)}
                      onCheckedChange={(checked) => handleSelectLog(log.id, checked as boolean)}
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <div className="font-medium">{log.userName}</div>
                    <div className="text-sm text-muted-foreground">{log.userEmail}</div>
                  </div>
                </TableCell>
                <TableCell className="font-medium">{log.habitName}</TableCell>
                <TableCell>
                  <Badge variant="outline">{t(`habits.categories.${log.category}`)}</Badge>
                </TableCell>
                <TableCell>
                  {log.organizationName || '-'}
                </TableCell>
                <TableCell>
                  {log.sectionName || '-'}
                </TableCell>
                <TableCell>
                  {log.groupName || '-'}
                </TableCell>
                <TableCell>
                  <div>
                    <div>{format(log.loggedAt, 'PP')}</div>
                    <div className="text-sm text-muted-foreground">
                      {formatDistanceToNow(log.loggedAt, { addSuffix: true })}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={log.isOrganizationDefault ? "default" : "secondary"}>
                    {log.isOrganizationDefault ? t('type.default') : t('type.personal')}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteConfirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteConfirm.message', { count: selectedLogs.size })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSelected}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('deleteConfirm.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
